import React, { ErrorInfo, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import Users from './users/Users';
import Login from './auth/Login';
import TwoFactorLogin from './auth/TwoFactorLogin';
import Organisations from './organisation/Organisations';
import TransactionAlerts from './alerts/TransactionAlerts';
import Feedback from './feedback/Feedback';
import Person from './aml/Person';
import RiskRules from './toolbox/risk/RiskRules';
import Tracker from './tracking/tracker';
import Dashboard from './dashboard/Dashboard';
import AttributeMappings from './schema/mappings/AttributeMappings';
import ResetPassword from './auth/ResetPassword';
import QueryTemplates from './toolbox/querytemplate/QueryTemplates';
import Webhooks from './webhooks/Webhooks';
import Dashboards from './dashboard/Dashboards';
import ScreeningAlert from './screening/alert/ScreeningAlert';
import ForgotPassword from './auth/ForgotPassword';
import ScreeningLists from './screening/ScreeningLists';
import DashboardsNotConfigured from './dashboard/DashboardsNotConfigured';
import ManualSanctionCheckPage from './screening/search/ManualSanctionCheckPage';
import ScreeningClearanceRules from './screening/clearance-rules/ScreeningClearanceRules';
import ScreeningClearanceRule from './screening/clearance-rules/ScreeningClearanceRule';
import ScreeningClearanceRulesSandbox from './screening/clearance-rules/sandbox/ScreeningClearanceRulesSandbox';
import ScreeningTestsSandbox from './screening/tests/ScreeningTestsSandbox';
import PrivateRoute from './route/PrivateRoute';
import { NoMatch } from './route/NoMatch';
import Profile from './profile/Profile';
import System from './system/System';
import Transaction from './transaction/Transaction';
import PersonAlerts from './alerts/PersonAlerts';
import MonitoringAlert from './alerts/MonitoringAlert';
import ScreeningAlertFilters from './screening/alert/filters/ScreeningAlertFilters';
import ScreeningTests from './screening/tests/ScreeningTests';
import { DataUpload } from './data-upload/DataUpload';
import ScreeningClearanceRulesAnalysis from './screening/clearance-rules/analyses/ScreeningClearanceRulesAnalysis';
import { FinalRiskOverrideReasons } from './risk/FinalRiskOverrideReasons';
import { SystemWarnings } from './system/SystemWarnings';
import AddCustomList from './custom-lists/AddCustomList';
import CustomLists from './custom-lists/CustomLists';
import CustomList from './custom-lists/CustomList';
import ApiConfiguration from './settings/api-configuration/ApiConfiguration';
import CustomListSettings from './custom-lists/CustomListSettings';
import AlertStatuses from './screening/alert-status/AlertStatuses';
import DecisionRules from './alerts/decision-rules/DecisionRules';
import AlertFilters from './alerts/filters/AlertFilters';
import ManualAlert from './alerts/manual/ManualAlert';
import './App.scss';
import BridgeAlert from './bridge/BridgeAlert';
import BulkClose from './alerts/bulk-close/BulkClose';
import BulkRiskOverride from './risk/bulk-override/BulkRiskOverride';
import ConfirmationModal from './confirmation-modal/ConfirmationModal';
import AlertStatusSetup from './screening/alert-status/setup/AlertStatusSetup';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorPage from './common/errors/ErrorPage';
import CustomListRecordPage from './custom-lists/record/CustomListRecordPage';
import { IntercomProvider } from 'react-use-intercom';
import { currentEnvironment } from './tracking/environment';
import { VersionWarnings } from './system/VersionWarnings';
import EntitySchemas from './schema/EntitySchemas';
import { useLocalisation } from './common/datetime/useLocalisation';
import MonitoringScenarios from './scenario/MonitoringScenarios';
import MonitoringScenario from './scenario/MonitoringScenario';
import RateLimiterConfig from './settings/rate-limit/RateLimiterConfig';
import Segments from './screening/configure/segments/Segments';
import DailyScreeningConfiguration from './screening/configure/daily-screening/DailyScreeningConfiguration';
import { useMe } from './organisation/useMe';
import { ScreenableFieldSetup } from './screening/fields/ScreenableFieldSetup';
import SignificantUpdateIndicators from './screening/configure/sui/SignificantUpdateIndicators';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import Features from './organisation/feature/Features';
import ListGroups from './screening/configure/lists/ListGroups';
import ScreeningFlows from './screening/configure/new/ScreeningFlows';
import FlowGroupDetail from './screening/configure/new/FlowGroupDetail';
import ScreeningFlowDraftTestRuns from './screening/tests/draft/ScreeningFlowDraftTestRuns';
import LibraryScenario from './scenario/LibraryScenario';
import Transliterator from './transliteration/Transliterator';
import {
  CAN_BULK_CLOSE_ALERTS,
  CAN_BULK_OVERRIDE_RISK,
  CAN_CREATE_CASE,
  CAN_EXECUTE_SANCTION_LIST_UPDATE,
  CAN_MODIFY_BRIDGE_SETTINGS,
  CAN_MODIFY_ORGANISATION_FEATURE,
  CAN_MODIFY_SEARCH_INDEX,
  CAN_MODIFY_USER,
  CAN_READ_ALERT_NOTE_TEMPLATE,
  CAN_READ_ALL_ORGANISATIONS,
  CAN_READ_CASE,
  CAN_READ_DECISION_RULES,
  CAN_READ_ENTITY_GROUP,
  CAN_READ_OAUTH_CLIENT,
  CAN_READ_RATE_LIMIT_CONFIG,
  CAN_READ_REVIEW_REQUESTS,
  CAN_READ_SCENARIO,
  CAN_READ_USER,
  CAN_READ_WEBHOOK,
  CAN_TEST_JMESPATH_EXPRESSION,
  CAN_TEST_SCREENING_CLEARANCE_RULE,
  CAN_USE_ENGINEERING_OPERATIONS,
  CAN_VIEW_ATTRIBUTE_MAPPING_MENU,
  CAN_VIEW_DASHBOARD_MENU,
  CAN_VIEW_SCHEMA_MENU,
  configurationViewPermissions,
} from './auth/permissions';
import { ORGANISATION_FEATURE } from './organisation/feature/helper';
import { SalvProgressCircle } from '@salvapp/ui-components';
import TransactionSearch from './search/TransactionSearch';
import Configuration from './settings/configuration/Configuration';
import { InternalNavigation } from './navigation/helper';
import Segment from './screening/configure/segments/Segment';
import TestRun from './scenario/versions/scenario-testing/TestRun';
import Navbar from './navigation/Navbar';
import domIds from './common/domIds';
import RiskRule from './risk/versions/RiskRule';
import PersonSearch from './search/PersonSearch';
import BridgeSettings from './bridge/BridgeSettings';
import JMESPathSandbox from './jmespath/sandbox/JMESPathSandbox';
import AlertNoteTemplateList from './alerts/note-templates/AlertNoteTemplateList';
import Cases from './case/Cases';
import Case from './case/Case';
import CaseCreation from './case/CaseCreation';
import EngineeringOps from './engineering-ops/EngineeringOps';
import ReviewRequests from './review-requests/ReviewRequests';

Tracker.initialize();

export const App = (): React.ReactElement => {
  const environment = currentEnvironment();
  const [unreadIntercomMessagesCount, setUnreadIntercomMessagesCount] = useState(0);

  useLocalisation();

  return (
    <>
      <Routes>
        <Route path={InternalNavigation.login} element={<Login />} />
        <Route path={InternalNavigation.twoFactor} element={<TwoFactorLogin />} />
        <Route path='/reset-password' element={<ResetPassword />} />
        <Route path='/forgot-password' element={<ForgotPassword />} />
        <Route path='/dashboards-not-configured' element={<DashboardsNotConfigured />} />
        <Route
          path={'*'}
          element={
            <IntercomProvider
              shouldInitialize={environment?.initializeIntercom}
              appId={'oymkiwu9'}
              apiBase={'https://api-iam.eu.intercom.io'}
              onUnreadCountChange={setUnreadIntercomMessagesCount}
            >
              <PrivateRoute>
                <Navbar unreadIntercomMessagesCount={unreadIntercomMessagesCount} />
                <div className='main-content' id={domIds.getMainContentId()}>
                  <ErrorBoundary
                    fallbackRender={ErrorPage}
                    onError={(error: unknown, info: ErrorInfo) => {
                      info.componentStack && Tracker.breadcrumb(info.componentStack);
                      Tracker.error(error);
                    }}
                  >
                    <Private />
                  </ErrorBoundary>
                </div>
              </PrivateRoute>
            </IntercomProvider>
          }
        />
      </Routes>
      <Feedback />
      <ConfirmationModal />
      <ReactQueryDevtools initialIsOpen={false} />
    </>
  );
};

const Private = () => {
  return (
    <>
      <SystemWarnings />
      <VersionWarnings />
      <div className={'page'}>
        <Routes>
          <Route path={InternalNavigation.scenarios} element={<MonitoringScenarios />} />
          <Route path='/person-scenarios' element={<Navigate to='/scenarios?entity=PERSON' />} />
          <Route path='/scenarios/add/:entity' element={<Navigate to='/scenarios' />} />
          <Route path='/scenarios/add/' element={<Navigate to='/scenarios' />} />
          <Route
            path={InternalNavigation.testRun}
            element={<ConditionalRoute element={<TestRun />} requiredPermissions={[CAN_READ_SCENARIO]} />}
          />
          <Route path='/scenarios/:handle' element={<MonitoringScenario />} />
          <Route path='/salv-scenarios/:handle' element={<LibraryScenario />} />
          <Route
            path={InternalNavigation.jmespathSandbox}
            element={
              <ConditionalRoute element={<JMESPathSandbox />} requiredPermissions={[CAN_TEST_JMESPATH_EXPRESSION]} />
            }
          />
          <Route path={InternalNavigation.riskOverrideReasons} element={<FinalRiskOverrideReasons />} />
          <Route path={InternalNavigation.risk} element={<RiskRules />} />
          <Route path={InternalNavigation.riskRule} element={<RiskRule />} />
          <Route path={InternalNavigation.queryTemplates} element={<QueryTemplates />} />
          <Route path={InternalNavigation.monitoringAlert} element={<MonitoringAlert />} />
          <Route path={InternalNavigation.bridgeAlert} element={<BridgeAlert />} />
          <Route path={InternalNavigation.monitoringTransactionAlerts} element={<TransactionAlerts />} />
          <Route path={InternalNavigation.monitoringPersonAlerts} element={<PersonAlerts />} />
          <Route path={InternalNavigation.dataUpload} element={<DataUpload />} />
          <Route
            path={InternalNavigation.apiConfiguration}
            element={<ConditionalRoute element={<ApiConfiguration />} requiredPermissions={[CAN_READ_OAUTH_CLIENT]} />}
          />
          <Route
            path={InternalNavigation.webhooks}
            element={<ConditionalRoute element={<Webhooks />} requiredPermissions={[CAN_READ_WEBHOOK]} />}
          />
          <Route path={InternalNavigation.person} element={<Person />} />
          <Route path={InternalNavigation.personSearch} element={<PersonSearch />} />
          <Route path={InternalNavigation.transactionSearch} element={<TransactionSearch />} />
          <Route path={InternalNavigation.dashboard} element={<Dashboard />} />
          <Route
            path={InternalNavigation.mappings}
            element={
              <ConditionalRoute
                element={<AttributeMappings />}
                requiredPermissions={[CAN_VIEW_ATTRIBUTE_MAPPING_MENU]}
              />
            }
          />
          <Route path={InternalNavigation.screeningFlows} element={<ConditionalRoute element={<ScreeningFlows />} />} />
          <Route path={InternalNavigation.screeningFlow} element={<ConditionalRoute element={<FlowGroupDetail />} />} />
          <Route
            path={InternalNavigation.screeningFlowDraftTestRuns}
            element={<ConditionalRoute element={<ScreeningFlowDraftTestRuns />} />}
          />
          <Route
            path={InternalNavigation.significantUpdateIndicators}
            element={<ConditionalRoute element={<SignificantUpdateIndicators />} />}
          />
          <Route
            path={InternalNavigation.automaticReScreening}
            element={<ConditionalRoute element={<DailyScreeningConfiguration />} />}
          />
          <Route path={InternalNavigation.listGroups} element={<ConditionalRoute element={<ListGroups />} />} />
          <Route path={InternalNavigation.screeningAlertFilters} element={<ScreeningAlertFilters />} />
          <Route
            path={InternalNavigation.screeningFlowWizard}
            element={<ConditionalRoute element={<ScreenableFieldSetup />} />}
          />
          <Route path={InternalNavigation.alertFilters} element={<AlertFilters />} />
          <Route path={InternalNavigation.manualAlert} element={<ManualAlert />} />
          <Route path={InternalNavigation.screeningSearch} element={<ManualSanctionCheckPage />} />
          <Route path={InternalNavigation.clearanceRules} element={<ScreeningClearanceRules />} />
          <Route path={InternalNavigation.clearanceRulesAdd} element={<ScreeningClearanceRule />} />
          <Route path={InternalNavigation.clearanceRule} element={<ScreeningClearanceRule />} />
          <Route path='/screening/clearance-rules-analyses/add/:entity' element={<ScreeningClearanceRulesAnalysis />} />
          <Route path={InternalNavigation.clearanceRulesAnalysis} element={<ScreeningClearanceRulesAnalysis />} />
          <Route
            path={InternalNavigation.clearanceRulesSandbox}
            element={
              <ConditionalRoute
                element={<ScreeningClearanceRulesSandbox />}
                requiredPermissions={[CAN_TEST_SCREENING_CLEARANCE_RULE]}
              />
            }
          />
          <Route path='/screening/alerts/:id/feed' element={<ScreeningAlert />} />
          <Route path={InternalNavigation.screeningAlert} element={<ScreeningAlert />} />
          <Route path={InternalNavigation.customLists} element={<CustomLists />} />
          <Route path={InternalNavigation.customListAdd} element={<AddCustomList />} />
          <Route path={InternalNavigation.customList} element={<CustomList />} />
          <Route path='/custom-lists/:customListId/settings' element={<CustomListSettings />} />
          <Route path='/custom-lists/:customListId/records' element={<CustomListRecordPage />} />
          <Route path='/custom-lists/:customListId/records/:recordId' element={<CustomListRecordPage />} />
          <Route
            path={InternalNavigation.cases}
            element={
              <ConditionalRoute
                element={<Cases />}
                requiredPermissions={[CAN_READ_CASE]}
                requiredFeatures={[ORGANISATION_FEATURE.CASE_MANAGEMENT]}
              />
            }
          />
          <Route
            path={InternalNavigation.caseCreation}
            element={
              <ConditionalRoute
                element={<CaseCreation />}
                requiredPermissions={[CAN_CREATE_CASE]}
                requiredFeatures={[ORGANISATION_FEATURE.CASE_MANAGEMENT]}
              />
            }
          />
          <Route
            path={InternalNavigation.caseDetails}
            element={
              <ConditionalRoute
                element={<Case />}
                requiredPermissions={[CAN_READ_CASE]}
                requiredFeatures={[ORGANISATION_FEATURE.CASE_MANAGEMENT]}
              />
            }
          />
          <Route path={InternalNavigation.screeningTests} element={<ScreeningTests />} />
          <Route path='/screening/sandbox' element={<ScreeningTestsSandbox />} />
          <Route path='/transliterator' element={<Transliterator />} />
          <Route
            path={InternalNavigation.schemas}
            element={<ConditionalRoute element={<EntitySchemas />} requiredPermissions={[CAN_VIEW_SCHEMA_MENU]} />}
          />
          <Route
            path={InternalNavigation.users}
            element={<ConditionalRoute element={<Users />} requiredPermissions={[CAN_READ_USER, CAN_MODIFY_USER]} />}
          />
          <Route
            path={InternalNavigation.screeningLists}
            element={
              <ConditionalRoute element={<ScreeningLists />} requiredPermissions={[CAN_EXECUTE_SANCTION_LIST_UPDATE]} />
            }
          />
          <Route path={InternalNavigation.alertStatuses} element={<AlertStatuses />} />
          <Route path='/screening/statuses/setup/:id?' element={<AlertStatusSetup />} />
          <Route
            path={InternalNavigation.bulkStatusChange}
            element={<ConditionalRoute element={<BulkClose />} requiredPermissions={[CAN_BULK_CLOSE_ALERTS]} />}
          />
          <Route
            path={InternalNavigation.bulkRiskOverride}
            element={<ConditionalRoute element={<BulkRiskOverride />} requiredPermissions={[CAN_BULK_OVERRIDE_RISK]} />}
          />
          <Route
            path={InternalNavigation.decisionRules}
            element={<ConditionalRoute element={<DecisionRules />} requiredPermissions={[CAN_READ_DECISION_RULES]} />}
          />
          <Route
            path={InternalNavigation.rateLimiter}
            element={
              <ConditionalRoute element={<RateLimiterConfig />} requiredPermissions={[CAN_READ_RATE_LIMIT_CONFIG]} />
            }
          />
          <Route path={InternalNavigation.transaction} element={<Transaction />} />
          <Route
            path={InternalNavigation.organisations}
            element={
              <ConditionalRoute element={<Organisations />} requiredPermissions={[CAN_READ_ALL_ORGANISATIONS]} />
            }
          />
          <Route
            path={InternalNavigation.configuration}
            element={<ConditionalRoute element={<Configuration />} anyPermissions={configurationViewPermissions} />}
          />
          <Route
            path={InternalNavigation.system}
            element={<ConditionalRoute element={<System />} requiredPermissions={[CAN_MODIFY_SEARCH_INDEX]} />}
          />
          <Route path={InternalNavigation.profile} element={<Profile />} />
          <Route
            path={InternalNavigation.features}
            element={
              <ConditionalRoute element={<Features />} requiredPermissions={[CAN_MODIFY_ORGANISATION_FEATURE]} />
            }
          />
          <Route
            path={InternalNavigation.segments}
            element={<ConditionalRoute element={<Segments />} requiredPermissions={[CAN_READ_ENTITY_GROUP]} />}
          />
          <Route
            path={InternalNavigation.segment}
            element={<ConditionalRoute element={<Segment />} requiredPermissions={[CAN_READ_ENTITY_GROUP]} />}
          />
          <Route
            path={InternalNavigation.dashboards}
            element={<ConditionalRoute element={<Dashboards />} requiredPermissions={[CAN_VIEW_DASHBOARD_MENU]} />}
          />
          <Route
            path={InternalNavigation.bridgeSettings}
            element={
              <ConditionalRoute
                element={<BridgeSettings />}
                requiredFeatures={[ORGANISATION_FEATURE.BRIDGE]}
                requiredPermissions={[CAN_MODIFY_BRIDGE_SETTINGS]}
              />
            }
          />
          <Route
            path={InternalNavigation.alertNoteTemplates}
            element={
              <ConditionalRoute
                element={<AlertNoteTemplateList />}
                requiredPermissions={[CAN_READ_ALERT_NOTE_TEMPLATE]}
              />
            }
          />
          <Route
            path={InternalNavigation.engineeringOps}
            element={
              <ConditionalRoute element={<EngineeringOps />} requiredPermissions={[CAN_USE_ENGINEERING_OPERATIONS]} />
            }
          />
          <Route
            path={InternalNavigation.reviewRequests}
            element={
              <ConditionalRoute
                element={<ReviewRequests />}
                requiredPermissions={[CAN_READ_REVIEW_REQUESTS]}
                requiredFeatures={[ORGANISATION_FEATURE.REVIEW_REQUESTS]}
              />
            }
          />
          <Route path='*' element={<NoMatch />} />
        </Routes>
      </div>
    </>
  );
};

export default App;

export interface ConditionalRouteProps {
  element: React.ReactElement;
  requiredPermissions?: string[];
  anyPermissions?: string[];
  requiredFeatures?: ORGANISATION_FEATURE[];
}

const ConditionalRoute = ({
  element,
  requiredPermissions,
  anyPermissions,
  requiredFeatures = [],
}: ConditionalRouteProps) => {
  const { permissions, features, isLoading: isCurrentUserDataLoading } = useMe();

  if (isCurrentUserDataLoading) {
    return (
      <div className='p-a-16'>
        <SalvProgressCircle />
      </div>
    );
  }

  const isAuthorised =
    (!requiredPermissions || requiredPermissions?.every((r) => permissions.includes(r))) &&
    (!anyPermissions || anyPermissions?.some((p) => permissions.includes(p))) &&
    requiredFeatures?.every((f) => features?.includes(f));

  return isAuthorised ? element : <NoMatch />;
};
